/**
 * Rides home helpers (docs/RIDES_UX_PLAN.md): the quick actions offered,
 * month groups, period totals and week-over-week change.
 */
import { isActivity } from './sports.ts';
import type { RideListItem } from '../types/api.ts';

export type RideAction = 'analyze' | 'openAnalysis' | 'askCoach' | 'rename' | 'delete';

/** The actions offered for ``ride``. */
export function rideActionsFor(ride: RideListItem): RideAction[] {
  if (isActivity(ride)) return ['rename', 'delete'];
  return [ride.coach_summary ? 'openAnalysis' : 'analyze', 'askCoach', 'rename', 'delete'];
}

/** A coach prompt about ``ride``, as on the app. */
export function askCoachPath(prompt: string): string {
  return `/?${new URLSearchParams({ prompt }).toString()}`;
}

/** The rides of one local month (``first`` = its first day, YYYY-MM-DD). */
interface MonthItems {
  first: string;
  items: RideListItem[];
}

/** ``items`` (newest first) split by local month. */
export function monthGroups(items: RideListItem[]): MonthItems[] {
  const groups: MonthItems[] = [];
  for (const item of items) {
    const d = new Date(item.started_at);
    const first = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
    const last = groups.at(-1);
    if (last?.first === first) last.items.push(item);
    else groups.push({ first, items: [item] });
  }
  return groups;
}

/** Whole-percent change; null when last week's value is 0. Under ±1 %: 0. */
export function percentChange(current: number, previous: number): number | null {
  if (previous === 0) return null;
  const percent = ((current - previous) / previous) * 100;
  return Math.abs(percent) < 1 ? 0 : Math.round(percent);
}

export interface WeekTotals {
  rides: number;
  hours: number;
  km: number;
  tss: number;
}

export function totalsFor(rides: RideListItem[]): WeekTotals {
  let durationSec = 0;
  let distanceM = 0;
  let tss = 0;
  for (const r of rides) {
    durationSec += r.duration_sec ?? 0;
    distanceM += r.distance_m ?? 0;
    tss += r.training_stress_score ?? 0;
  }
  return {
    rides: rides.length,
    hours: durationSec / 3600,
    km: distanceM / 1000,
    tss,
  };
}

